import { useEffect, useState } from 'react'
import { Alert, Box, Button, CircularProgress, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/SaveOutlined'
import { horairesParDefaut, type Horaires } from '@iobiz/shared'
import { api } from '../../api/client.js'
import { useSessionStore } from '../../stores/sessionStore.js'
import { HorairesOuverture } from './HorairesOuverture.js'

/**
 * Les jours ouvrés de la boutique — onglet Agenda du module Paramètres.
 *
 * ⭐ Ces horaires appartiennent à l'enseigne et vivent dans SON schéma. Ils
 * étaient jusqu'ici dans la console de la plateforme : le gérant ne pouvait pas
 * déclarer qu'il ferme le lundi sans passer par l'exploitant, alors que ce sont
 * eux qui décident des créneaux offerts dans son agenda.
 *
 * ⚠️ La session est mise à jour sur place après enregistrement. Les horaires y
 * sont embarqués pour que la prise de rendez-vous fonctionne HORS LIGNE : sans
 * ce rafraîchissement, l'agenda aurait continué de proposer les anciens
 * créneaux jusqu'à la prochaine connexion.
 */
export function ReglagesAgenda({ storeId }: { storeId: string }): JSX.Element {
  const majHoraires = useSessionStore((s) => s.majHorairesBoutique)
  const [horaires, setHoraires] = useState<Horaires | null>(null)
  const [envoi, setEnvoi] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; texte: string } | null>(null)

  useEffect(() => {
    void (async () => {
      try {
        const r = await api.get<{ horaires: Horaires | null }>(`/stores/${storeId}/reglages`)
        // ⚠️ Jamais réglés : on part du modèle courant plutôt que d'une grille
        // vide. C'est déjà ce que l'agenda applique en attendant — afficher
        // sept journées fermées laisserait croire que la boutique n'ouvre pas.
        setHoraires(r.horaires ?? horairesParDefaut())
      } catch (e) {
        setMessage({ type: 'error', texte: e instanceof Error ? e.message : 'Lecture impossible.' })
        setHoraires(horairesParDefaut())
      }
    })()
  }, [storeId])

  if (!horaires) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 200 }}>
        <CircularProgress size={28} />
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1 }}>
        Jours et heures d’ouverture
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 4 }}>
        Ils déterminent les créneaux proposés dans l’agenda. Un jour fermé n’en propose aucun.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 4 }} onClose={() => setMessage(null)}>
          {message.texte}
        </Alert>
      )}

      <HorairesOuverture valeur={horaires} onChange={setHoraires} />

      <Box sx={{ mt: 5 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={envoi}
          onClick={async () => {
            setEnvoi(true)
            setMessage(null)
            try {
              const r = await api.patch<{ horaires: Horaires | null }>(
                `/stores/${storeId}/reglages`,
                { horaires },
              )
              if (r.horaires) majHoraires(storeId, r.horaires)
              setMessage({ type: 'success', texte: 'Horaires enregistrés.' })
            } catch (e) {
              setMessage({
                type: 'error',
                texte: e instanceof Error ? e.message : 'Enregistrement impossible.',
              })
            } finally {
              setEnvoi(false)
            }
          }}
        >
          Enregistrer
        </Button>
      </Box>
    </Box>
  )
}
